/**
 * propertyLinkCascade — registry-delete link CASCADE (owner bug 2026-07-03).
 *
 * Trashing (or archiving) a registry property only flagged the property doc;
 * every project that referenced it kept BOTH link lanes intact:
 *   · Lane B — `projects/{pid}.propertyRefs[]` (the registry link), and
 *   · Lane A — `intake/{tier}.answers.properties[i].propertyId` (the candidate
 *     stamp), so Intake kept showing "✓ auto-linked P#####" for a property that
 *     no longer exists in any list.
 *
 * This module fans the removal out to every referencing project, using the
 * property doc's `engagementRefs[]` back-references:
 *   1. `unlinkPropertyFromProject` — the ONE existing Lane-B decision point
 *      (removes the project's `propertyRefs[]` entry + this property's
 *      `engagementRefs[]` entry, read-modify-write, shape-tolerant);
 *   2. `clearCandidatePropertyStamp` — the Lane-A inverse (strips the candidate
 *      stamp via the same `saveProperties` array-replace every candidate write
 *      uses; a stamp that was never set is a clean no-op).
 *
 * BEST-EFFORT by contract: the trash/archive flag has already landed before the
 * cascade runs; a per-project failure is console.warn'd and the loop continues
 * (older data with empty/absent `engagementRefs` degrades to a no-op — never a
 * crash, never a blocked delete). Sequential per-project ops through the two
 * existing single-decision-point helpers, NOT a hand-rolled batch — one write
 * path per lane, zero drift.
 */
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { unlinkPropertyFromProject } from './projectStore';
import { clearCandidatePropertyStamp } from './staffIntake';
import { toDeliverableTier } from './reportDelivery';

/**
 * PURE — the unique, usable projectIds out of a property doc's
 * `engagementRefs[]`. Tolerates absent / empty / garbage-shaped arrays (older
 * data) by returning [] — the cascade then no-ops.
 */
export function referencedProjectIds(engagementRefs: unknown): string[] {
  if (!Array.isArray(engagementRefs)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of engagementRefs) {
    const pid =
      r && typeof r === 'object'
        ? (r as Record<string, unknown>).projectId
        : null;
    if (typeof pid !== 'string' || !pid || seen.has(pid)) continue;
    seen.add(pid);
    out.push(pid);
  }
  return out;
}

/**
 * PURE — a project doc's display tier (e.g. "L1A · Selection Path…") → the
 * intake doc id its candidate stamps live under (e.g. "L1A_R1"), or null when
 * the tier is not a wired deliverable tier. Mirrors ProjectDetail's own
 * derivation (bare code before the "·"/"•" separator → `toDeliverableTier` →
 * `${tier}_R1`).
 */
export function intakeTierForProjectTier(
  tier: string | null | undefined,
): string | null {
  const bare = String(tier || '')
    .split(/\s*[·•]/)[0]
    .trim()
    .toUpperCase();
  const deliverable = toDeliverableTier(bare);
  return deliverable ? `${deliverable}_R1` : null;
}

/**
 * Fan a registry property's removal out to every referencing project — Lane B
 * (registry link) + Lane A (candidate stamp) both cleared. Never throws:
 * every failure path is console.warn'd (the caller's trash/archive has already
 * succeeded and must stay succeeded).
 */
export async function cascadePropertyRemoval(propertyId: string): Promise<void> {
  let projectIds: string[] = [];
  try {
    const snap = await getDoc(doc(db, 'properties', propertyId));
    if (!snap.exists()) return;
    projectIds = referencedProjectIds(
      (snap.data() as Record<string, unknown>).engagementRefs,
    );
  } catch (e) {
    console.warn(
      `[cascadePropertyRemoval] could not read ${propertyId} (links may be stale):`,
      (e as Error).message,
    );
    return;
  }

  for (const projectId of projectIds) {
    // Lane B — registry link (projects.propertyRefs[] + engagementRefs[]).
    try {
      await unlinkPropertyFromProject(projectId, propertyId);
    } catch (e) {
      console.warn(
        `[cascadePropertyRemoval] unlink ${propertyId} ⇠ ${projectId} failed:`,
        (e as Error).message,
      );
    }
    // Lane A — the candidate stamp on that project's intake doc. Needs the
    // project's clientId + tier to address the intake; absent/unknown → skip
    // (nothing stamped to clear on a project without a wired intake tier).
    try {
      const projSnap = await getDoc(doc(db, 'projects', projectId));
      if (!projSnap.exists()) continue;
      const d = projSnap.data() as Record<string, unknown>;
      const clientId =
        (typeof d.clientId === 'string' && d.clientId) ||
        (typeof d.clientNo === 'string' && d.clientNo) ||
        '';
      const intakeTier = intakeTierForProjectTier(
        typeof d.tier === 'string' ? d.tier : '',
      );
      if (!clientId || !intakeTier) continue;
      await clearCandidatePropertyStamp({ clientId, projectId }, intakeTier, propertyId);
    } catch (e) {
      console.warn(
        `[cascadePropertyRemoval] stamp clear ${propertyId} ⇠ ${projectId} failed:`,
        (e as Error).message,
      );
    }
  }
}
